<script>
  import { createEventDispatcher } from "svelte";
  import Modal from "@components/Modal.svelte";
  import ExcelFileIcon from "@components/common/icons/app/ExcelFileIcon.svelte";
  import InfoIcon from "@components/common/icons/app/InfoIcon.svelte";
  import * as XLSX from "xlsx";
  import { DataTable } from "carbon-components-svelte";
  import { Select, Button } from "paxray-components-svelte"
  import { ARROW_LEFT_ICON, CLOSE_ICON, UPLOAD_ICON } from '@utils/icons.js';
  import { downloadProjectTemplate, TEMPLATE_SHEETS } from "@utils/downloads.js";

  export let open = false;
  export let loading = false;
  export let maxFileSizeMb = 25;
  export let acceptedTypes = [".xlsx", ".xls"];
  export let heading = "Import Framework";
  export let fieldDefinitions = null;
  export let fileDescription = "that contains the framework data.";
  export let mappingDescription = "Select which columns from your file correspond to the required fields below.";

  const dispatch = createEventDispatcher();

  let files = [];
  let dropzone;

  let currentStep = 1;
  let dropError = "";
  let tableLoading = false;

  let excelColumns = [];
  let previewByColumn = {};
  let parsedRows = [];
  let previewRows = [];

  const requiredFields = [
    {
      key: "department_l0",
      label: "Department (L0)",
      required: false,
      patterns: ["department l0", "department", "team"],
    },
    {
      key: "process_l1",
      label: "Process (L1)",
      required: true,
      patterns: ["process l1", "l1", "process"],
    },
    {
      key: "subprocess_l2",
      label: "Subprocess (L2)",
      required: true,
      patterns: ["subprocess l2", "sub process l2", "l2", "subprocess"],
    },
    {
      key: "task_l3",
      label: "Task (L3)",
      required: true,
      patterns: ["task l3", "l3", "task column", "task"],
    },
    {
      key: "subtask_l4",
      label: "Subtask (L4)",
      required: true,
      patterns: ["subtask l4", "sub task l4", "l4", "subtask"],
    },
  ];

  $: importFields = fieldDefinitions || requiredFields;
  // Other importers (e.g. license data) pass their own fields and read the first sheet.
  $: isFrameworkImport = !fieldDefinitions;

  let columnMapping = {};

  $: requiredMappingsValid = importFields
    .filter((field) => field.required)
    // .every((field) => columnMapping[field.key]);
    .every((field) => {
      const value = columnMapping[field.key];
      return value && String(value).trim() !== "";
    });

  $: previewHeaders = importFields
    .filter((field) => columnMapping[field.key] && !field.hidden)
    .map((field) => ({ key: field.key, value: field.label }));

  $: previewTableRows = previewRows.map((row, i) => {
    const mapped = { id: String(i) };
    for (const field of importFields) {
      if (columnMapping[field.key] && !field.hidden) {
        mapped[field.key] = row[field.key] || "";
      }
    }
    return mapped;
  });

  $: selectedFile = files?.[0] ?? null;
  $: isPrimaryDisabled = loading || !selectedFile;
  $: acceptedFileTypeText = acceptedTypes.join(", ");

  function normalizeFiles(event) {
    dropError = "";
    const detailFiles = event?.detail?.files;
    const inputFiles = event?.target?.files;

    if (Array.isArray(detailFiles)) {
      files = detailFiles;
      return;
    }

    if (inputFiles) {
      files = Array.from(inputFiles);
      return;
    }

    files = [];
  }

  function openNativePicker() {
    const input = dropzone?.querySelector('input[type="file"]');
    input?.click();
  }

  function handleSecondaryClick() {
    files = [];
    open = false;
    dispatch("close");
    currentStep = 1;
  }

  function handleClose() {
    files = [];
    dispatch("close");
    currentStep = 1;
  }

  async function handlePrimaryAction() {
    if (currentStep === 1) {
      if (!selectedFile || loading) return;
      try {
        loading = true;
        await parseExcelFile(selectedFile);
        currentStep = 2;
      } catch (error) {
        console.error("Error reading Excel file:", error);
      } finally {
        loading = false;
      }
      return;
    }

    if (currentStep === 2) {
      if (!requiredMappingsValid) return;
      previewRows = parsedRows.slice(0, 5).map((row) => {
        const mapped = {};
        for (const field of importFields) {
          const col = columnMapping[field.key];
          mapped[field.key] = col ? (row[col] ?? "") : "";
        }
        return mapped;
      });
      currentStep = 3;
      tableLoading = true;
      setTimeout(() => {
        tableLoading = false;
      }, 1000);
      return;
    }

    const mappedRows = parsedRows.map((row) => {
      const mapped = {};
      for (const field of importFields) {
        const col = columnMapping[field.key];
        if (col) mapped[field.key] = (row[col] ?? "").toString().trim();
      }
      return mapped;
    });

    dispatch("submit", {
      rows: mappedRows,
    });
  }

  function handleBack() {
    if (currentStep > 1) {
      currentStep -= 1;
    } else if (currentStep === 1) {
      files = [];
      const input = dropzone?.querySelector('input[type="file"]');
      if (input) input.value = "";
    }
  }

  function removeSelectedFile(event) {
    event?.stopPropagation();
    files = [];
    dropError = "";
    const input = dropzone?.querySelector('input[type="file"]');
    if (input) input.value = "";
  }

  function handleDragOver(event) {
    event.preventDefault();
  }

  function handleDrop(event) {
    event.preventDefault();
    const droppedFiles = event.dataTransfer?.files;
    if (droppedFiles?.length) {
      const file = droppedFiles[0];
      const ext = "." + file.name.split(".").pop().toLowerCase();
      if (!acceptedTypes.includes(ext)) {
        dropError = `File type "${ext}" is not allowed. Accepted: ${acceptedFileTypeText}`;
        files = [];
        return;
      }
      dropError = "";
      files = Array.from(droppedFiles);
    }
  }

  async function parseExcelFile(file) {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });

    // The project template starts with an instructions sheet, so use its
    // framework sheet when it is there.
    const sheetName =
      isFrameworkImport && workbook.SheetNames.includes(TEMPLATE_SHEETS.framework)
        ? TEMPLATE_SHEETS.framework
        : workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];

    const rawRows = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      defval: "",
      raw: false,
    }).filter((row) =>
      row.some((value) => String(value ?? "").trim() !== ""),
    );

    if (!rawRows.length) {
      parsedRows = [];
      excelColumns = [];
      previewByColumn = {};
      return;
    }

    const firstRow = rawRows[0].map((value) => String(value ?? "").trim());
    const headerPatterns = importFields.flatMap((field) =>
      (field.patterns || [field.label]).map(normalizeHeader),
    );
    const hasHeader = firstRow.some((value) =>
      headerPatterns.some((pattern) =>
        pattern && normalizeHeader(value).includes(pattern),
      ),
    );
    const fallbackHeaders = [
      "Department (L0)",
      "Process (L1)",
      "Subprocess (L2)",
      "Task (L3)",
      "Subtask (L4)",
    ];
    const headers = hasHeader
      ? firstRow
      : firstRow.map((_, index) => fallbackHeaders[index] || `Column ${index + 1}`);
    const dataRows = hasHeader ? rawRows.slice(1) : rawRows;

    parsedRows = dataRows.map((row) =>
      Object.fromEntries(
        headers.map((header, index) => [
          header,
          String(row[index] ?? "").trim(),
        ]),
      ),
    );

    excelColumns = headers.map((header) => ({
      value: header,
      text: header,
    }));

    previewByColumn = Object.fromEntries(
      headers.map((header) => {
        const first3 = parsedRows
          .map((row) => row[header])
          .filter(
            (value) =>
              value !== null &&
              value !== undefined &&
              String(value).trim() !== "",
          )
          .slice(0, 3)
          .map((value) => String(value).trim());

        return [header, first3];
      }),
    );

    columnMapping = buildInitialMapping(headers);
  }

  function normalizeHeader(value) {
    return String(value)
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  function buildInitialMapping(headers) {
    const normalizedHeaders = headers.map((header) => ({
      original: header,
      normalized: normalizeHeader(header),
    }));

    const findHeader = (patterns) => {
      const match = normalizedHeaders.find(({ normalized }) =>
        patterns.some((pattern) => normalized.includes(pattern)),
      );
      return match?.original ?? "";
    };

    return Object.fromEntries(
      importFields.map((field) => [
        field.key,
        findHeader(field.patterns || [normalizeHeader(field.label)]),
      ]),
    );
  }
</script>

<Modal
  bind:open
  actions
  autowidth
  preventCloseOnClickOutside
  modalHeading={heading}
  primaryButtonText={currentStep === 3 ? "Import" : "Next"}
  secondaryButtonText="Cancel"
  primaryButtonAction={handlePrimaryAction}
  primaryButtonNotAllow={currentStep === 1
    ? loading || !selectedFile
    : currentStep === 2
      ? !requiredMappingsValid
      : tableLoading}
  primaryButtonValidations={currentStep === 1
    ? loading || !selectedFile
    : currentStep === 2
      ? !requiredMappingsValid
      : tableLoading}
  on:click:button--secondary={handleSecondaryClick}
  on:close={handleClose}
>
  <div class="import-framework-modal">
    <div class="steps" aria-label="Import steps">
      <div
        class="step"
        class:step--active={currentStep === 1}
        class:step--done={currentStep > 1}
      >
        <span class="step__bullet">{currentStep > 1 ? "✓" : "1"}</span>
        <span class="step__label">Upload File</span>
      </div>

      <div class="step__line" aria-hidden="true"></div>

      <div
        class="step"
        class:step--active={currentStep === 2}
        class:step--done={currentStep > 2}
      >
        <span class="step__bullet">{currentStep > 2 ? "✓" : "2"}</span>
        <span class="step__label">Map Columns</span>
      </div>

      <div class="step__line" aria-hidden="true"></div>

      <div class="step" class:step--active={currentStep === 3}>
        <span class="step__bullet">3</span>
        <span class="step__label">Preview & Import</span>
      </div>
    </div>

    {#if currentStep === 1}
      <div class="copy-block">
        <h3>Upload your Excel file</h3>
        <p>Upload an Excel file (.xlsx) {fileDescription}</p>
      </div>

      <div
        class="dropzone-shell"
        bind:this={dropzone}
        on:dragover|preventDefault={handleDragOver}
        on:drop|preventDefault={handleDrop}
      >
        <input
          type="file"
          accept={acceptedTypes.join(",")}
          hidden
          on:change={normalizeFiles}
        />
        <div class="dropzone-overlay">
          {#if loading}
            <p class="dropzone-title">Uploading</p>
            <p class="dropzone-separator">please wait</p>
          {:else if selectedFile}
            <div class="selected-file-header">
              <Button
                iconDescription="Remove selected file"
                on:click={removeSelectedFile}
                icon={CLOSE_ICON}
                size="small"
                rounded
                variant="secondary"
              />
            </div>
            <div class="excel-icon">
              <ExcelFileIcon size={52} />
            </div>
            <p class="dropzone-filename">{selectedFile.name}</p>
          {:else}
            <ExcelFileIcon size={52} />
            <p class="dropzone-title">Drag and drop your file here</p>
            <p class="dropzone-separator">or</p>
            <Button
              size="field"
              kind="secondary"
              text="Choose File"
              iconLeft
              icon={UPLOAD_ICON}
              on:click={openNativePicker}
            />
          {/if}
        </div>
      </div>
      {#if dropError}
        <p class="drop-error">{dropError}</p>
      {/if}

      <div class="meta-text">
        <span>Accepted file type: {acceptedFileTypeText}</span>
        <span>Maximum file size: {maxFileSizeMb} MB</span>
      </div>

      {#if isFrameworkImport}
        <div class="template-hint">
          <span>
            No file yet? Use our project template and fill in the sheet
            "{TEMPLATE_SHEETS.framework}".
          </span>
          <Button
            kind="outline-dark"
            size="field"
            text="Download Template"
            on:click={downloadProjectTemplate}
          />
        </div>
      {/if}
    {:else if currentStep === 2}
      <div class="copy-block">
        <h3>Map Excel columns to required fields</h3>
        <p>{mappingDescription}</p>
      </div>

      <div class="mapping-table">
        <div class="mapping-table__head">
          <div>Required Field</div>
          <div>Map to Excel Column</div>
          <div>Preview (first 3 rows)</div>
        </div>

        {#each importFields as field}
          <div class="mapping-table__row">
            <div class="mapping-table__field">
              {field.label}{field.required ? " *" : ""}
            </div>

            <div>
               <Select
                items={excelColumns}
                bind:selected={columnMapping[field.key]}
                placeholder="Select column"
              />
            </div>

            <div class="mapping-table__preview">
              {#if columnMapping[field.key]}
                {(previewByColumn[columnMapping[field.key]] || []).join(", ")}
              {:else}
                —
              {/if}
            </div>
          </div>
        {/each}
      </div>

      <p class="required-note">* Required fields</p>

      <div class="info-box">
        <InfoIcon size={18} />
        Please ensure all required fields are mapped before continuing.
      </div>
    {:else if currentStep === 3}
      <div class="copy-block">
        <h3>Preview and import data</h3>
        <p>
          Review your data before importing. You can go back to adjust the
          column mapping if needed.
        </p>
      </div>

      <div class="preview-table-wrapper">
        <DataTable
          class="custom-table zebra-rows"
          headers={previewHeaders}
          rows={previewTableRows}
        />
      </div>

      <p class="meta-text info">
        Showing first {previewRows.length} rows of {parsedRows.length}
      </p>
      <div class="info-box">
        <InfoIcon size={18} />
        {isFrameworkImport
          ? "New entries are added to the framework. Existing entries with the same name are kept."
          : "The import will create new records based on the mapped columns."}
      </div>
    {/if}
  </div>
  <svelte:fragment slot="action-buttons">
    <!-- Only from step 2 on, step 1 has no previous step -->
    {#if currentStep > 1}
      <Button
        text="Back"
        on:click={handleBack}
        kind="secondary"
        size="field"
        iconLeft
        icon={ARROW_LEFT_ICON}
      />
    {/if}
  </svelte:fragment>
</Modal>

<style>
  .import-framework-modal {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
    min-width: 46rem;
    padding-top: 0.25rem;
    font-family: Inter, sans-serif;
  }

  .steps {
    display: grid;
    grid-template-columns: auto 1fr auto 1fr auto;
    align-items: center;
    gap: 0.75rem;
  }

  .step {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #a8a8a8;
    min-width: max-content;
  }

  .step--active {
    color: #161616;
  }

  .step__bullet {
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6875rem;
    font-weight: 600;
    background: #e8e8e8;
    color: #6f6f6f;
    padding-top: 2px;
  }

  .step--active .step__bullet {
    background: #161616;
    color: #ffffff;
  }

  .step__label {
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .step__line {
    height: 1px;
    background: #d9d9d9;
    width: 100%;
  }

  .step--done {
    color: #161616;
  }

  .step--done .step__bullet {
    background: #161616;
    color: #ffffff;
  }

  .copy-block h3 {
    margin: 0 0 0.5rem;
    font-size: 0.9375rem;
    font-weight: 700;
    color: #161616;
  }

  .copy-block p {
    margin: 0;
    font-size: 0.8125rem;
    color: #6f6f6f;
  }

  .dropzone-shell {
    position: relative;
    min-height: 12rem;
    border: 1px dashed #d9d9d9;
    border-radius: 0.25rem;
    background: #ffffff;
    overflow: hidden;
  }

  .dropzone-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    background: transparent;
    padding: 1.5rem;
  }

  .dropzone-title,
  .dropzone-separator {
    margin: 0;
    font-size: 0.875rem;
    color: #525252;
  }

  .dropzone-separator {
    font-size: 0.75rem;
    color: #8d8d8d;
  }

  .dropzone-filename {
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #161616;
    margin-top: -1rem;
  }

  .meta-text {
    display: flex;
    flex-direction: column;
    gap: 0.1875rem;
    font-size: 0.75rem;
    color: #8d8d8d;
  }

  .template-hint {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--control-gap);
    font-size: 0.8125rem;
    color: #525252;
  }

  .meta-text.info {
    margin-top: 0;
  }

  .mapping-table {
    border-top: 1px solid #e0e0e0;
  }

  .mapping-table__head,
  .mapping-table__row {
    display: grid;
    grid-template-columns: 1.2fr 1.6fr 1.8fr;
    gap: 1rem;
    align-items: start;
  }

  .mapping-table__head {
    padding: 0.75rem 0;
    font-size: 0.75rem;
    font-weight: 600;
    color: #525252;
    border-bottom: 1px solid #e0e0e0;
  }

  .mapping-table__row {
    padding: 1rem 0;
    border-bottom: 1px solid #e0e0e0;
  }

  .mapping-table__field {
    font-size: 0.875rem;
    font-weight: 500;
    color: #161616;
  }

  .mapping-table__preview {
    font-size: 0.8125rem;
    color: #525252;
    line-height: 1.4;
  }

  .drop-error {
    margin: 0;
    font-size: 0.75rem;
    color: #da1e28;
  }

  .required-note {
    margin: 0;
    font-size: 0.75rem;
    color: #6f6f6f;
  }

  .info-box {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.875rem 1rem;
    border: 1px solid #d9d9d9;
    border-radius: 0.375rem;
    background: #f8f8f8;
    font-size: 0.8125rem;
    color: #525252;
  }

  .selected-file-header {
    margin-left: 4rem;
  }

  .excel-icon {
    margin-top: -1rem;
  }

  :global(.import-framework-modal .bx--file) {
    width: 100%;
  }

  :global(.import-framework-modal .bx--file-container) {
    width: 100%;
  }

  :global(.import-framework-modal .bx--file__drop-container) {
    min-height: 12rem;
    border: 0;
    background: transparent;
    justify-content: center;
    padding: 1.5rem;
  }

  :global(.import-framework-modal .bx--file-browse-btn),
  :global(.import-framework-modal .bx--label),
  :global(.import-framework-modal .bx--file--label),
  :global(.import-framework-modal .bx--file__selected-file) {
    display: none;
  }

  :global(.import-framework-modal .bx--file-input) {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
    z-index: 2;
  }

  .preview-table-wrapper {
    overflow-x: auto;
  }

  :global(.dropzone-shell p) {
    padding-right: 0 !important;
  }

  :global(
      .custom-table.zebra-rows .bx--data-table tbody tr:nth-child(even) td
    ) {
    background-color: #f4f4f4 !important;
  }

  :global(.custom-table.zebra-rows .bx--data-table tbody tr:nth-child(odd) td) {
    background-color: #ffffff !important;
  }

  :global(.custom-table.zebra-rows .bx--data-table) {
    border-left: 1px solid #e0e0e0 !important;
    border-right: 1px solid #e0e0e0 !important;
    border-radius: 0.5rem !important;
    overflow: hidden !important;
    border-collapse: separate !important;
    border-spacing: 0 !important;
  }

  @media (max-width: 52rem) {
    .import-framework-modal {
      min-width: auto;
    }

    .steps {
      grid-template-columns: 1fr;
      gap: 0.5rem;
    }

    .step__line {
      display: none;
    }
  }
</style>
